/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.

For any inquiry, please contact Colibryx S.r.l. at info@colibryx.com.
*/

import { animate, style, transition, trigger } from "@angular/animations";
import { CommonModule, DatePipe } from "@angular/common";
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	input,
	output,
} from "@angular/core";
import { ButtonComponent } from "@app/widgets/base/button/button.component";
import { IconComponent } from "@app/widgets/base/icon/icon.component";
import { FeaturedIconComponent } from "@app/widgets/base/misc-icons/featured-icon/featured-icon.component";
import { TranslateModule } from "@ngx-translate/core";
import { v4 } from "uuid";

export interface NotificationAction {
	label: string;
	color: "brand" | "success" | "warning" | "error" | "info" | "gray";
	callback: () => void;
}

@Component({
	selector: "cb-notification",

	imports: [
		CommonModule,
		IconComponent,
		FeaturedIconComponent,
		DatePipe,
		ButtonComponent,
		TranslateModule,
	],
	templateUrl: "./notification.component.html",
	styleUrl: "./notification.component.scss",
	changeDetection: ChangeDetectionStrategy.OnPush,
	animations: [
		trigger("showHide", [
			transition(":enter", [
				style({ opacity: 0, transform: "translateY(10%)" }),
				animate(
					"0.1s ease-in-out",
					style({ opacity: 1, transform: "translateY(0)" }),
				),
			]),
			transition(":leave", [
				animate(
					"0.1s ease-in-out",
					style({ opacity: 0, transform: "translateY(10%)" }),
				),
			]),
		]),
	],
})
export class NotificationComponent {
	/**
	 * The unique key of the notification.
	 */
	public readonly key = input(v4());

	/**
	 * If the notification should use inner html.
	 */
	public readonly useInnerHtml = input(false);

	/**
	 * The color of the notification.
	 */
	public readonly color = input<
		"brand" | "success" | "warning" | "error" | "info" | "gray"
	>("brand");

	/**
	 * The hierarchy of the notification.
	 */
	public readonly hierarchy = input<"secondary" | "tertiary">("tertiary");

	/**
	 * The icon of the notification.
	 */
	public readonly icon = input("");

	/**
	 * The title of the notification.
	 */
	public readonly title = input("");

	/**
	 * The direction of the notification.
	 */
	public readonly direction = input<"row" | "col">("col");

	/**
	 * The message of the notification.
	 */
	public readonly message = input("");

	/**
	 * The actions of the notification.
	 */
	public readonly actions = input<NotificationAction[]>([]);

	/**
	 * The datetime at which the notification was created.
	 */
	public readonly createdAt = input<Date | string>("");

	/**
	 * An image to preview in the notification.
	 */
	public readonly image = input("");

	/**
	 * If the close button should be shown.
	 */
	public readonly showCloseButton = input(false);

	/**
	 * The style of the image
	 */
	public readonly imageStyle = input<"thumbnail" | "preview">("thumbnail");

	/**
	 * The action clicked event.
	 */
	public readonly actionClicked = output<NotificationAction>();

	/**
	 * The close clicked event.
	 */
	public readonly closeClicked = output();

	public currentIcon = computed(() => {
		const icon = this.icon();
		if (icon) return icon;

		switch (this.color()) {
			case "success":
				return "cb-base-outline-checkmark-circle";
			case "warning":
				return "cb-base-outline-alert-circle";
			case "error":
				return "cb-base-outline-alert-circle";
			default:
				return "cb-base-outline-info-circle";
		}
	});

	public currentIconType = computed(() => {
		switch (this.hierarchy()) {
			case "secondary":
				return "light-outline";
			case "tertiary":
				return "outline";
			default:
				return "outline";
		}
	});
}
